/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { useUCSuggestions } from '@atlassian/navigation-bff-data-provider';

import { useMenuControl } from '../../../context';
import {
	useSuggestionExcludeAtis,
	useSuggestionNativeAtis,
	useSuggestionPreferredAtis,
	useUniversalCreateAnalytics,
} from '../../../hooks';
import { GenericErrorBoundary } from '../../../services';
import ItemWrapper from '../MenuItem/item-wrapper';
import MenuSectionPrimitive from '../MenuSectionPrimitive';
import { SkeletonItem } from '../SkeletonItem';
import { SuggestionItem } from '../SuggestionItem';

const messages = defineMessages({
	sectionHeading: {
		id: 'platform.navigation-experiences.universal-create.suggestion.section-heading',
		defaultMessage: '{count, plural, one {Suggestion} other {Suggestions}}',
		description: 'The heading for the suggestion menu section',
	},
});

function SuggestionMenuSectionContent() {
	const { formatMessage, locale } = useIntl();
	const { mixedItems, product, suggestionConfig, subProduct } = useMenuControl();

	const cloudId = suggestionConfig.enabled ? suggestionConfig.cloudId : '';
	const orgId = suggestionConfig.enabled ? suggestionConfig.orgId : '';
	const userId = suggestionConfig.enabled ? suggestionConfig.accountId : '';
	const compact = suggestionConfig.enabled && !!suggestionConfig.compact;
	const location = suggestionConfig.enabled ? suggestionConfig.location : 'end';

	// Use the shared hooks for ATIs logic
	const nativeAtis = useSuggestionNativeAtis();
	const preferredAtis = useSuggestionPreferredAtis();
	const excludeAtis = useSuggestionExcludeAtis();

	const { data, isLoading, hasError } = useUCSuggestions({
		cloudId,
		orgId,
		userId,
		product,
		subProduct,
		locale,
		nativeAtis,
		preferredAtis,
		excludeAtis,
	});

	const { fireNewSuggestionsMenuViewed_old, fireNewSuggestionsMenuViewed } =
		useUniversalCreateAnalytics();

	useEffect(() => {
		const items = data?.items;
		if (items?.length) {
			const suggestedItems = items.map((item) => item.ati);
			if (fg('navx-2236-unified-instrumentation-updates')) {
				fireNewSuggestionsMenuViewed({
					crossAppSuggestionsEnabled: suggestionConfig.enabled || false,
					suggestionsGroupCount: 0,
					suggestionsItemCount: items.length,
					suggestionGroupList: [
						{
							// Note: Currently only one suggestion section is supported, and more button is not in use
							groupHeading: formatMessage(messages.sectionHeading, { count: items.length }),
							groupIndex: 0,
							groupItemAtisList: suggestedItems,
							groupItemCount: items.length,
						},
					],
				});
			} else {
				fireNewSuggestionsMenuViewed_old({
					crossAppSuggestionsEnabled: suggestionConfig.enabled || false,
					suggestionMenuGroup: {
						displayModeCompact: compact,
						itemAtis: suggestedItems,
						suggestionLocation: location,
						showMoreButton: false,
					},
					totalSuggestionItems: items.length,
				});
			}
		}
	}, [
		data,
		fireNewSuggestionsMenuViewed,
		fireNewSuggestionsMenuViewed_old,
		formatMessage,
		compact,
		location,
		suggestionConfig.enabled,
	]);

	if (suggestionConfig.enabled && isLoading) {
		return (
			<ItemWrapper isMenuItem={!fg('navx-enghealth-47001-suggestion-loading-semantics')}>
				<SkeletonItem testId="loading-suggestion-section" />
			</ItemWrapper>
		);
	}

	if (!suggestionConfig.enabled || hasError || !data?.items?.length) {
		return null;
	}

	const items = data.items;

	return (
		<MenuSectionPrimitive
			sectionIndex={location === 'start' ? 0 : mixedItems.length + 1}
			itemLength={items.length}
			showAll
			compact={compact}
			heading={formatMessage(messages.sectionHeading, { count: items.length })}
			isNative={false}
		>
			{items.map((responseItem, index) => (
				<SuggestionItem
					key={`suggestion-menu-item-${responseItem.ati}`}
					responseItem={responseItem}
					context={suggestionConfig}
					product={product}
					subProduct={subProduct}
					itemIndex={index}
					totalItemsCount={items.length}
				/>
			))}
		</MenuSectionPrimitive>
	);
}

export default function SuggestionMenuSection() {
	return (
		<GenericErrorBoundary name="suggestion-menu-section">
			<SuggestionMenuSectionContent />
		</GenericErrorBoundary>
	);
}
